/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { useParams } from 'js/lib/useRouter';

import { Button, Grid, Typography, Typography2, breakpoints } from '@coursera/cds-core';

import TakeACourseBtn from 'bundles/degree-description/components/modules/hero/TakeACourseBtn';
import Container from 'bundles/degree-description/components/shared/Container';
import { useDegreeDescriptionPageTheme } from 'bundles/degree-description/degreeDescriptionPageTheme';
import { useDegreeDescriptionEventingContext } from 'bundles/degree-description/providers/DegreeDescriptionEventingProvider';
import type { UpsellsByDegreeSlug } from 'bundles/degree-description/types';
import type { EoiStateOverrides, EoiStateVariants } from 'bundles/degree-description/utils/experiments';
import type { PersonalizedMessage } from 'bundles/degree-description/utils/personalizedMessageContent';
import { getEoiButton } from 'bundles/degree-description/utils/personalizedMessageCriteria';
import { shouldShowEoiStateVariant } from 'bundles/degree-description/utils/utils';
import EoiButton from 'bundles/expression-of-interest/components/EoiButton';
import { INTERACTION_EVENTS_MAP } from 'bundles/expression-of-interest/constants';
import type { RetrieveCompletedSurveyResponse } from 'bundles/expression-of-interest/queries/RetrieveCompletedQualificationSurvey';
import type { EoiButtonsType } from 'bundles/expression-of-interest/types';
import { useEoiEventing } from 'bundles/expression-of-interest/utils/eventingUtils';
import { appendUtmParametersToApplicationLink } from 'bundles/expression-of-interest/utils/util';
import TrackedButton from 'bundles/page/components/TrackedButton';
import TrackedDiv from 'bundles/page/components/TrackedDiv';
import { TrackedLink2 } from 'bundles/page/components/TrackedLink2';
import LazyImgix from 'bundles/page/components/shared/LazyImgix';

import image from 'images/degree-description/embedded-module-image.png';

import _t from 'i18n!nls/degree-description';

const styles = {
  xddpWrap: css`
    padding-left: 0;
    padding-right: 0;
  `,
  background: css`
    ${breakpoints.up('md')} {
      padding: 48px 0;
    }
    ${breakpoints.down('sm')} {
      padding-bottom: 24px;
    }
  `,
  description: css`
    color: var(--cds-color-neutral-primary-invert);
    padding-right: 16px;

    h3,
    p {
      color: var(--cds-color-neutral-primary-invert);
    }

    a {
      /* Override for CDS links */
      color: var(--cds-color-neutral-primary-invert);

      &:visited:not(.cds-link-focusVisible) {
        color: var(--cds-color-neutral-primary-invert);
      }

      &:hover,
      &:focus {
        background-color: transparent;
        color: var(--cds-color-neutral-primary-invert);
      }
    }

    a,
    button {
      display: inline-block;
      margin-left: -16px;
      width: auto;
    }

    ${breakpoints.down('sm')} {
      margin-top: var(--cds-spacing-400);
      padding-right: 0;
    }
  `,
  darkDescription: css`
    color: inherit;

    h3,
    p {
      color: inherit;
    }

    a,
    button {
      margin-left: 0;
    }
  `,
  image: css`
    ${breakpoints.up('md')} {
      max-width: 510px;
    }

    margin-left: auto;
    margin-right: auto;
  `,
  hideOnMobile: css`
    ${breakpoints.down('sm')} {
      display: none;
    }
  `,
  hideOnDesktop: css`
    ${breakpoints.up('md')} {
      display: none;
    }
  `,
};

type Props = {
  personalizedMessage: PersonalizedMessage;
  eoiButtons?: EoiButtonsType;
  trackingIndex?: number;
  setEoiTrackingIndex?: React.Dispatch<React.SetStateAction<number>>;
  ctaAction?: (() => void) | string | null;
  onXddp?: boolean;
  eoiSurveyData?: RetrieveCompletedSurveyResponse;
  stackableItemsWithUpsellType?: UpsellsByDegreeSlug;
  eoiStateVarState?: EoiStateVariants;
  showVariantOverrides?: EoiStateOverrides;
};

export const PersonalizedEmbeddedMsg: React.FunctionComponent<Props> = ({
  personalizedMessage,
  eoiButtons,
  trackingIndex,
  setEoiTrackingIndex,
  ctaAction,
  onXddp,
  eoiSurveyData,
  stackableItemsWithUpsellType,
  eoiStateVarState,
  showVariantOverrides,
}) => {
  const theme = useDegreeDescriptionPageTheme();
  const { slug } = useParams();
  const { useGetViewPersonalizedMessageRef, trackingProductId } = useDegreeDescriptionEventingContext();
  const { trackInteractEoi } = useEoiEventing(trackingProductId);
  const viewRef = useGetViewPersonalizedMessageRef(personalizedMessage.id);
  const { title, body, ctaText, ctaActionType } = personalizedMessage;

  const messageData = personalizedMessage;

  const buttonType = onXddp ? 'primary' : 'ghostInvert';
  const trackingName = ctaActionType === 'ENROLL_APPLY' ? 'apply' : 'email_me_info';

  const eoiButtonConfig = getEoiButton(ctaActionType, ctaText, buttonType, eoiButtons);

  const trackEoiButtonClick = () => {
    if (setEoiTrackingIndex) {
      setEoiTrackingIndex(trackingIndex || 1);
    }
    trackInteractEoi(INTERACTION_EVENTS_MAP.emailMeInfo.v3EventKey, null, trackingIndex, personalizedMessage.id);
  };

  let eoiActionElement = eoiButtonConfig && (
    <EoiButton
      actionConfig={eoiButtonConfig}
      trackingIndex={trackingIndex}
      setEoiTrackingIndex={setEoiTrackingIndex}
      trackingProductId={trackingProductId}
    />
  );

  if (
    shouldShowEoiStateVariant(
      slug,
      stackableItemsWithUpsellType,
      showVariantOverrides,
      eoiSurveyData,
      eoiStateVarState,
      'B'
    )
  ) {
    eoiActionElement = <TakeACourseBtn slug={slug} variantOverride="ghost" />;
  } else if (
    shouldShowEoiStateVariant(
      slug,
      stackableItemsWithUpsellType,
      showVariantOverrides,
      eoiSurveyData,
      eoiStateVarState,
      'A'
    )
  ) {
    return null;
  }

  return (
    <div css={styles.background} ref={viewRef}>
      <Container css={onXddp ? styles.xddpWrap : undefined}>
        <TrackedDiv
          trackingName="personalized_message_embedded"
          trackClicks={false}
          data={messageData}
          withVisibilityTracking
        >
          <Grid container justifyContent="space-between" alignItems="center">
            <Grid
              item
              container
              direction="column"
              spacing={24}
              sm={12}
              md={onXddp ? 12 : 6}
              css={[styles.description, onXddp ? styles.darkDescription : undefined]}
            >
              <Grid item>
                <Typography variant="h1semibold" component="h3" css={theme.mobileFontSizeStyle?.header}>
                  {title}
                </Typography>
              </Grid>
              <Grid item>
                <Typography2 component="p">{body}</Typography2>
              </Grid>
              <Grid item>
                {eoiActionElement}
                {!eoiButtonConfig && ctaAction && (
                  <React.Fragment>
                    {typeof ctaAction === 'string' ? (
                      <Button
                        component={TrackedLink2}
                        data={messageData}
                        variant={buttonType}
                        href={appendUtmParametersToApplicationLink(ctaAction)}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={() => trackEoiButtonClick()}
                        trackingName={trackingName}
                        size="small"
                      >
                        {ctaText}
                      </Button>
                    ) : (
                      <Button
                        component={TrackedButton}
                        data={messageData}
                        variant={buttonType}
                        onClick={() => {
                          trackEoiButtonClick();
                          ctaAction();
                        }}
                        trackingName={trackingName}
                        withVisibilityTracking={false}
                        requireFullyVisible={false}
                        size="small"
                      >
                        {ctaText}
                      </Button>
                    )}
                  </React.Fragment>
                )}
              </Grid>
            </Grid>

            {image && !onXddp && (
              <Grid item sm={12} md={6} css={[styles.image, styles.hideOnMobile]}>
                <LazyImgix
                  src={image}
                  maxWidth={510}
                  maxHeight={295}
                  // eslint-disable-next-line no-restricted-syntax
                  alt={_t('Embedded module image')}
                  enableFluidWidth={false}
                />
              </Grid>
            )}
          </Grid>
        </TrackedDiv>
      </Container>
    </div>
  );
};

export default PersonalizedEmbeddedMsg;
